import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, Undo2 } from "lucide-react";

const formatCOP = (n) => `$ ${Math.round(n || 0).toLocaleString("es-CO")}`;

/** Marca un producto como "costo correcto, no revisar más". Devuelve true si se marcó. */
export async function marcarCostoRevisado(productoId, nombre) {
  const nota = window.prompt(`"${nombre}"\n\nConfirmas que el costo del lote es REAL (no es un paquete sin desglosar). Dejará de aparecer en las revisiones de costo; la utilidad seguirá mostrando la pérdida/ganancia real.\n\nNota (opcional, ej. "se compró caro, se vendió con pérdida"):`, "");
  if (nota === null) return false;
  try {
    await api.post(`/productos/${productoId}/costo-revisado`, { nota });
    toast.success(`${nombre}: marcado como costo correcto`);
    return true;
  } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo marcar"); return false; }
}

export function BotonCostoCorrecto({ productoId, nombre, onDone, testid }) {
  return (
    <button type="button" title="Costo correcto, no revisar más"
      onClick={async (ev) => { ev.stopPropagation(); if (await marcarCostoRevisado(productoId, nombre)) onDone?.(); }}
      className="inline-flex items-center gap-1 h-7 px-2 rounded-md border border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-[10px] font-semibold whitespace-nowrap"
      data-testid={testid}>
      <ShieldCheck className="w-3 h-3" /> Costo correcto
    </button>
  );
}

/** Lista plegable de productos marcados como revisados, con "Deshacer". */
export function CostoRevisadoLista({ total, onCambio }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!open) return;
    api.get("/productos/_/costo-revisado").then((r) => setData(r.data)).catch(() => setData({ total: 0, productos: [] }));
  }, [open, total]);
  if (!total) return null;
  const deshacer = async (p) => {
    try {
      await api.delete(`/productos/${p.producto_id}/costo-revisado`);
      toast.success(`${p.producto}: vuelve a revisarse`);
      setData(null); onCambio?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo deshacer"); }
  };
  return (
    <div className="text-[11px]" data-testid="costo-revisado-lista">
      <button type="button" onClick={() => setOpen((o) => !o)} className="text-primary hover:underline" data-testid="costo-revisado-toggle">
        {open ? "Ocultar" : "Ver"} {total} producto(s) marcado(s) como "costo correcto" · deshacer
      </button>
      {open && data && (
        <ul className="mt-2 rounded-md border border-border divide-y divide-border/60">
          {data.productos.map((p, i) => (
            <li key={p.producto_id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-2 py-1.5" data-testid={`costo-revisado-item-${i}`}>
              <span className="font-medium flex-1 min-w-[160px]">{p.producto}{p.codigo && <span className="text-muted-foreground"> · {p.codigo}</span>}</span>
              <span className="text-muted-foreground tabular-nums">ficha {formatCOP(p.costo_ficha)} · mín. {formatCOP(p.precio_minimo)}</span>
              <span className="text-muted-foreground">{p.por} · {(p.at || "").slice(0, 10)}{p.nota ? ` · "${p.nota}"` : ""}</span>
              <button type="button" onClick={() => deshacer(p)} className="inline-flex items-center gap-1 text-rose-700 hover:underline" data-testid={`costo-revisado-deshacer-${i}`}><Undo2 className="w-3 h-3" /> Deshacer</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
